/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import user from 'js/lib/user';

import { Grid, Typography2 } from '@coursera/cds-core';
import { ChevronDownIcon, ChevronUpIcon } from '@coursera/cds-icons';

import { truncate } from 'bundles/enterprise-ui/utils';

import _t from 'i18n!nls/page';

type Props = {
  header: string;
  label: string;
  profileImage: JSX.Element;
  isActive?: boolean;
  isCaretUp?: boolean;
  onClick: () => void;
};

const styles = {
  navigationHeader: css`
    align-items: center;
    background-color: var(--cds-color-white-0);
    border: none;
    border-left: 1px solid var(--cds-color-grey-50);
    display: flex;
    flex-direction: row;
    padding: 0 16px 0 20px;
    height: 100%;
    width: 100%;
    cursor: pointer;
  `,
  textContainer: css`
    text-align: left;
    margin: 0 12px;
  `,
  navigationLabel: css`
    text-transform: capitalize;
  `,
  svg: css`
    display: block;
  `,
};

const NavigationHeader = ({ header, label, profileImage, isActive, isCaretUp, onClick }: Props) => {
  const ariaLabel = isCaretUp
    ? _t('Admin dropdown menu for #{adminName}: open', { adminName: user.get().full_name })
    : _t('Admin dropdown menu for #{adminName}: closed', { adminName: user.get().full_name });

  return (
    <button
      type="button"
      className={`${isActive ? 'active' : ''}`}
      css={styles.navigationHeader}
      aria-haspopup="true"
      aria-expanded={isCaretUp}
      aria-label={ariaLabel}
      data-e2e="Navigation_Header"
      data-pendo-guide="NavigationHeader"
      data-testid="Navigation_Header"
      onClick={onClick}
    >
      <Grid container direction="row" wrap="nowrap" alignItems="center">
        {profileImage}
        <Grid container direction="column" css={styles.textContainer}>
          <Typography2 variant="subtitleMedium" component="div" data-testid="label">
            {header}
          </Typography2>
          <Typography2
            component="p"
            variant="bodySecondary"
            color="supportText"
            css={styles.navigationLabel}
            aria-label={label}
          >
            {truncate(label, 55)}
          </Typography2>
        </Grid>
        <Grid item>
          {isCaretUp ? (
            <ChevronUpIcon size="medium" css={styles.svg} />
          ) : (
            <ChevronDownIcon size="medium" css={styles.svg} />
          )}
        </Grid>
      </Grid>
    </button>
  );
};

export default NavigationHeader;
